import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import { IntakeSectionHeader, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import DynamicForm from "@/common/DynamicForm";
import { staticFields } from "@/util/common_variable";
import { PERSON } from "../InmateDetails/Personal";
import { getVisibleFields, isNonEmptyVariable, hasCheckSecurity, convertApiFields, getSQLTypeToHTMLType } from "@/util/common_function";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import DetentionAliasesList from "./DetentionAliasesList";
import PropTypes from "prop-types";

const DETENTION_BIO_FIELD_NAMES = [
  "lName",
  "fName",
  "mName",
  "suffix",
  "ssn",
  "dob",
  "race",
  "sex",
  "height",
  "weight",
  "eyes",
  "ethnicity",
  "dlNumber",
  "dlState",
  "dlExpiration",
];

let _detentionBioDynamicFields = null;

const getDetentionBioDynamicFields = () => {
  if (_detentionBioDynamicFields) return _detentionBioDynamicFields;

  const bookingsBioFields = getVisibleFields(
    staticFields.personalInfo,
    null,
    PERSON.BOOKINGS
  );

  _detentionBioDynamicFields = [
    ...DETENTION_BIO_FIELD_NAMES.map((columnName) =>
      bookingsBioFields.find(
        ({ columnName: fieldColumnName }) => fieldColumnName === columnName
      )
    )
      .filter(Boolean)
      .map((field) => ({ ...field })),
    {
      columnName: "sid",
      type: "varchar(100)",
      tableName: "Bio",
      label: "SID",
    },
    {
      columnName: "fbi",
      type: "varchar(100)",
      tableName: "Bio",
      label: "FBI",
    },
    {
      // ASP: RecordStructureBio.asp:1192-1196 - Place_Of_Birth_Country
      // shares the same DropDownSource ("POBNCIC") as Country_of_Citizenship,
      // not a free-text field. type: "bit" is this codebase's existing
      // (if odd) convention for triggering DynamicForm's select rendering -
      // see the "state" field below for the same pattern.
      columnName: "placeOfBirthCountry",
      type: "bit",
      refListName: "POBNCIC",
      isID: true,
      tableName: "Bio",
      label: "Country of Birth",
    },
    {
      columnName: "streetNumber",
      type: "varchar(20)",
      tableName: "Bio",
      label: "#",
    },
    {
      columnName: "street1",
      type: "varchar(200)",
      tableName: "Bio",
      label: "Street",
    },
    {
      columnName: "street2",
      type: "varchar(200)",
      tableName: "Bio",
      label: "Street 2",
    },
    {
      columnName: "apartmentNumber",
      type: "varchar(50)",
      tableName: "Bio",
      label: "Apt #",
    },
    {
      columnName: "city",
      type: "varchar(100)",
      tableName: "Bio",
      label: "City",
    },
    {
      refListName: "USState",
      columnName: "state",
      type: "bit",
      tableName: "Bio",
      label: "ST",
      isID: false,
    },
    {
      columnName: "zip",
      type: "varchar(10)",
      tableName: "Bio",
      label: "Zip Code",
    },
  ];

  return _detentionBioDynamicFields;
};


const normalizeAliasForState = (alias = {}) => ({
  ...alias,
  questionedIdentity:
    typeof alias?.questionedIdentity === "string"
      ? alias.questionedIdentity.toLowerCase() === "true"
      : !!alias?.questionedIdentity,
});

const mapApiAliasesToState = (aliases = []) =>
  Array.isArray(aliases) ? aliases.map(normalizeAliasForState) : [];

const mapStateAliasesToApi = (aliases = []) =>
  Array.isArray(aliases)
    ? aliases.map(({ id, questionedIdentity, ...rest }) => ({
        ...rest,
        questionedIdentity:
          typeof questionedIdentity === "string"
            ? questionedIdentity.toLowerCase() === "true"
            : !!questionedIdentity,
      }))
    : [];

const buildFormValues = (bio = {}) => {
  if (!isNonEmptyVariable(bio) || Object.keys(bio).length === 0) return {};

  return {
    ...bio,
    dlNumber: bio?.dlNumber ?? bio?.dlnumber ?? null,
    dob: isNonEmptyVariable(bio?.dob) ? dayjs(bio.dob) : null,
    dlExpiration: isNonEmptyVariable(bio?.dlExpiration)
      ? dayjs(bio.dlExpiration)
      : null,
    placeOfBirthCountry: bio?.placeOfBirthCountry ?? null,
  };
};

// extraFieldKeys/extraTypeMap: DetentionsBioExtra's dynamic field keys (see
// the component below) — stripped out of `values` here so they never reach
// `onUpdateForm`/the main detentionsBio[0] payload the parent eventually
// POSTs to detentions/update; returned separately as extraSnapshot for the
// caller to save via the generic extra-table endpoint instead.
const formatPayload = (
  values = {},
  aliases = [],
  currentBio = {},
  extraFieldKeys = [],
  extraTypeMap = {},
) => {
  const cleanValues = { ...values };
  const extraSnapshot = {};
  extraFieldKeys.forEach((k) => {
    if (k in cleanValues) {
      const val = cleanValues[k];
      const htmlType = extraTypeMap[k];
      extraSnapshot[k] = dayjs.isDayjs(val)
        ? val.format(htmlType === "datetime" ? "MM/DD/YYYY HH:mm" : "MM/DD/YYYY")
        : val;
      delete cleanValues[k];
    }
  });

  const payload = {
    ...currentBio,
    ...cleanValues,
    dob: cleanValues?.dob ? dayjs(cleanValues.dob).format("MM/DD/YYYY") : null,
    dlExpiration: cleanValues?.dlExpiration
      ? dayjs(cleanValues.dlExpiration).format("MM/DD/YYYY")
      : null,
    detentionsBioAliases: mapStateAliasesToApi(aliases),
  };

  if ("dlNumber" in payload) {
    payload.dlnumber = payload.dlNumber;
  }

  return { payload, extraSnapshot };
};

const DetentionsBioForm = forwardRef(
  ({ bio = {}, personalFormRef, onUpdateForm = () => {}, disabled = false, startingPoint="" }, ref) => {
    const [form] = Form.useForm();
    const isSettingInitialValues = useRef(false);
    const [aliases, setAliases] = useState(
      mapApiAliasesToState(bio?.detentionsBioAliases || [])
    );

    // -----------------------------------------------------------------------
    // DetentionsBioExtra — PCI's per-client dynamic "_U" columns for this
    // detention's Bio record (BioInterface.asp's
    // CreateHTMLInterfaceBioSimpleDetentions renders this as "Additional
    // Personal Information", right at the end of the Simple Bio block).
    // -----------------------------------------------------------------------
    const [detentionsBioExtraFields, setDetentionsBioExtraFields] = useState([]);
    const [detentionsBioExtraFieldsReady, setDetentionsBioExtraFieldsReady] = useState(false);
    const detentionsBioExtraTypeMapRef = useRef({});
    const detentionsBioExtraIdRef = useRef(0);
    const detentionsBioExtraSnapshotRef = useRef(null);

    useEffect(() => {
      api
        .get(`${API_ENDPOINTS.getTableColumns}/DetentionsBioExtra`)
        .then((response) => {
          const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
          const converted = convertApiFields(tableData, "DetentionsBioExtra");
          const typeMap = {};
          converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
          detentionsBioExtraTypeMapRef.current = typeMap;
          setDetentionsBioExtraFields(converted);
          setDetentionsBioExtraFieldsReady(true);
        })
        .catch((error) => {
          console.error("[DetentionsBioForm] DetentionsBioExtra field defs fetch failed:", error);
        });
    }, []);

    // Fetches the saved extra row once a real detentionBioID is present
    // (editing an existing detention) — a brand new one has no id yet, so
    // there's nothing to fetch until it's saved once. The raw API's own PK
    // for a detention's Bio record is "detentionBioID" (confirmed against
    // a live payload) — NOT "detentionsBioId" as bookingTree.js/
    // detentionTree.js's primaryKey metadata claims; that mismatch was why
    // this fetch (and the corresponding save in EditDetentionsModal.jsx/
    // pages/Detentions/index.jsx) never found an id to use.
    useEffect(() => {
      if (!detentionsBioExtraFieldsReady) return;
      const detentionBioId = bio?.detentionBioID;
      if (!isNonEmptyVariable(detentionBioId)) {
        detentionsBioExtraIdRef.current = 0;
        return;
      }

      fetchExtraTableRecord("DetentionsBioExtra", "detentionBioID", detentionBioId)
        .then((result) => {
          if (!result) return;
          detentionsBioExtraIdRef.current = result.raw?.DetentionBioExtraID ?? 0;
          const withDates = Object.keys(result.values).reduce((acc, key) => {
            const val = result.values[key];
            const htmlType = detentionsBioExtraTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {});
          form.setFieldsValue(withDates);
        })
        .catch((error) => {
          console.error("[DetentionsBioForm] DetentionsBioExtra fetch failed:", error);
        });
    }, [bio?.detentionBioID, detentionsBioExtraFieldsReady]);

    useEffect(() => {
      if (isNonEmptyVariable(bio) && Object.keys(bio).length > 0) {
        isSettingInitialValues.current = true;
        form.setFieldsValue(buildFormValues(bio));
        setAliases(mapApiAliasesToState(bio?.detentionsBioAliases || []));
      } else {
        form.resetFields();
        setAliases([]);
      }
    }, [bio, form]);

    useImperativeHandle(ref || personalFormRef, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      validateFields: () => form.validateFields(),
      form,
      // DetentionsBioExtra snapshot/type map/current PK — read by the
      // parent chain (AddDetention -> pages/Detentions/index.jsx) after the
      // main detentions/update save succeeds, to save it separately via the
      // generic extra-table endpoint.
      getDetentionsBioExtraSnapshot: () => detentionsBioExtraSnapshotRef.current,
      detentionsBioExtraTypeMap: detentionsBioExtraTypeMapRef.current,
      detentionsBioExtraID: detentionsBioExtraIdRef.current,
    }));

    const emitUpdate = (values, updatedAliases = aliases) => {
      const extraFieldKeys = detentionsBioExtraFields.map((f) => f.columnName);
      const { payload, extraSnapshot } = formatPayload(
        values,
        updatedAliases,
        bio,
        extraFieldKeys,
        detentionsBioExtraTypeMapRef.current,
      );
      detentionsBioExtraSnapshotRef.current =
        extraFieldKeys.length > 0 ? extraSnapshot : null;
      onUpdateForm(payload);
    };

    const handleAliasesChange = (updatedAliases = []) => {
      setAliases(updatedAliases);
      emitUpdate(form.getFieldsValue(), updatedAliases);
    };

    return (
      <>
        <IntakeSectionHeader style={{ marginTop: "0px" }}>
          <span>Personal Information</span>
        </IntakeSectionHeader>

        <Form
          form={form}
          layout="vertical"
          disabled={disabled}
          onValuesChange={(_, allValues) => {
            if (isSettingInitialValues.current) {
              isSettingInitialValues.current = false;
              return;
            }
            emitUpdate(allValues);
          }}
          onBlur={() => emitUpdate(form.getFieldsValue())}
        >
          <StyledGridRow gutter={[16, 0]}>
            {getDetentionBioDynamicFields().map((field, index) => {
              let view = hasCheckSecurity(
                "Functionality",
                "field",
                "VIEW",
                "",
                "",
                "DetentionsBio",
                field.columnName,
                startingPoint
              );
              let edit = hasCheckSecurity(
                "Functionality",
                "field",
                "MODIFY",
                "",
                "",
                "DetentionsBio",
                field.columnName,
                startingPoint
              );
              return(
                <DynamicForm
                  key={field.columnName || index}
                  formAttributes={field}
                  i={index}
                  form={form}
                  person={PERSON.BOOKINGS}
                  bookingsFieldsEnabled={!disabled}
                  view={view}
                  edit={edit ? !disabled : false}
                />
              );
          })}
          </StyledGridRow>

          <DetentionAliasesList
            aliases={aliases}
            disabled={disabled}
            onChange={handleAliasesChange}
            startingPoint={startingPoint}
          />

          {detentionsBioExtraFields.length > 0 && (
            <FieldSecurityProvider
              entity="DetentionsBioExtra"
              startingPoint={startingPoint}
              bookingsFieldsEnabled={!disabled}
            >
              <StyledSectionHeading marginTop="20px">
                <span>Additional Personal Information</span>
              </StyledSectionHeading>
              <StyledGridRow gutter={[16, 0]}>
                {detentionsBioExtraFields.map((sf, index) => (
                  <DynamicForm
                    key={index}
                    formAttributes={sf}
                    form={form}
                    bookingsFieldsEnabled={!disabled}
                  />
                ))}
              </StyledGridRow>
            </FieldSecurityProvider>
          )}
        </Form>
      </>
    );
  }
);
DetentionsBioForm.displayName = "DetentionsBioForm";

DetentionsBioForm.propTypes = {
  bio: PropTypes.object,
  personalFormRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]),
  onUpdateForm: PropTypes.func,
  disabled: PropTypes.bool,
};

export default DetentionsBioForm;
